import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Slider from "@mui/material/Slider";
import {
  Container,
  SliderTrack,
  SliderSegment,
  SliderThumb,
  SliderLabels,
  SliderValues,
  ValueItem,
} from "./styles";
import { FormatCurrency, roundCurrency } from "../../lib";

export default function SliderCheckoutCoupon({
  totalPoints = 0,
  totalMoney = 0,
  couponDiscount = 0,
  userBalance = 0,
  minPoints = 0,
  onChange,
  disabled = false,
  isCouponApplied = false,
  factor = 1,
}) {
  const [sliderValue, setSliderValue] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);

  const calculations = useMemo(() => {
    const safeTotal = Number(totalMoney) || 0;
    const safeCoupon = Number(couponDiscount) || 0;
    const safeBalance = Number(userBalance) || 0;
    const safeTotalPoints = Number(totalPoints) || 0;
    const safeMinPoints = Number(minPoints) || 0;
    const safeFactor = Number(factor) || 1;

    const effectiveCoupon = Math.min(safeCoupon, safeTotal);
    const totalAfterCoupon = Math.max(0, safeTotal - effectiveCoupon);
    const discountInPoints = Math.round(effectiveCoupon * safeFactor);
    const totalPointsAfterCoupon = Math.max(
      0,
      safeTotalPoints - discountInPoints,
    );
    const maxPointsFromBalance = Math.min(safeBalance, totalPointsAfterCoupon);
    const balanceInMoney =
      safeFactor > 0 ? maxPointsFromBalance / safeFactor : 0;
    const minMoneyRequired = Math.max(0, totalAfterCoupon - balanceInMoney);

    let maxMoneyAllowed = totalAfterCoupon;
    if (safeMinPoints > 0 && totalPointsAfterCoupon > 0) {
      const minPointsInMoney = safeFactor > 0 ? safeMinPoints / safeFactor : 0;
      maxMoneyAllowed = Math.max(0, totalAfterCoupon - minPointsInMoney);
    }

    const couponPercentage =
      safeTotal > 0 ? (effectiveCoupon / safeTotal) * 100 : 0;

    let scenario = 1;
    if (minMoneyRequired > 0) {
      scenario = 3;
    } else if (balanceInMoney >= totalAfterCoupon) {
      scenario = 1;
    } else {
      scenario = 2;
    }

    return {
      effectiveCoupon,
      totalAfterCoupon,
      minMoneyRequired,
      maxMoneyAllowed,
      couponPercentage,
      maxPointsFromBalance,
      balanceInMoney,
      scenario,
      safeTotalPoints,
      safeTotal,
      safeFactor,
      safeMinPoints,
      discountInPoints,
      totalPointsAfterCoupon,
    };
  }, [totalMoney, couponDiscount, userBalance, totalPoints, minPoints, factor]);

  useEffect(() => {
    if (isCouponApplied && calculations.safeTotal > 0) {
      const initialMoneyValue = calculations.minMoneyRequired;
      const initialSliderValue =
        calculations.totalAfterCoupon > 0
          ? (initialMoneyValue / calculations.totalAfterCoupon) * 100
          : 0;
      setSliderValue(Math.max(0, Math.min(100, initialSliderValue)));
    }
  }, [isCouponApplied, calculations]);

  const currentValues = useMemo(() => {
    if (calculations.totalAfterCoupon <= 0) {
      return {
        moneyToPay: 0,
        pointsToUse: 0,
        moneyPercentage: 0,
        pointsPercentage: 100 - calculations.couponPercentage,
      };
    }

    // Manter valores com precisão total durante cálculos
    // Arredondar apenas no final para evitar erro acumulado
    let moneyToPay = (sliderValue / 100) * calculations.totalAfterCoupon;

    // Threshold para tratar valores residuais muito pequenos (< R$ 0,05) como zero
    // Isso resolve o problema de centavos residuais devido a arredondamentos
    const MONEY_THRESHOLD = 0.05;

    if (moneyToPay < MONEY_THRESHOLD && moneyToPay > 0) {
      moneyToPay = 0;
    }

    // Calcula pontos corretamente: subtrai o equivalente em pontos do dinheiro que será pago
    // Em vez de usar ratio que introduz erro de arredondamento
    const isFullPoints = sliderValue === 0 || moneyToPay === 0;
    const pointsToUse = isFullPoints
      ? calculations.totalPointsAfterCoupon
      : calculations.totalPointsAfterCoupon -
        Math.round(moneyToPay * calculations.safeFactor);

    // Calcular porcentagens baseadas no total APÓS cupom
    // para que os segmentos de pontos e dinheiro sejam proporcionais ao espaço disponível
    const moneyPercentage =
      calculations.totalAfterCoupon > 0
        ? (moneyToPay / calculations.totalAfterCoupon) * 100
        : 0;
    const pointsPercentage = 100 - moneyPercentage;

    // Ajustar para considerar o cupom no total visual
    // As porcentagens de pontos e dinheiro devem ocupar apenas (100% - couponPercentage)
    const availablePercentage = 100 - calculations.couponPercentage;
    const adjustedMoneyPercentage = (moneyPercentage * availablePercentage) / 100;
    const adjustedPointsPercentage = availablePercentage - adjustedMoneyPercentage;

    return {
      moneyToPay,
      pointsToUse,
      moneyPercentage: Math.max(0, Math.min(100, adjustedMoneyPercentage)),
      pointsPercentage: Math.max(0, Math.min(100, adjustedPointsPercentage)),
    };
  }, [sliderValue, calculations]);

  const sliderLimits = useMemo(() => {
    const minSlider =
      calculations.totalAfterCoupon > 0
        ? (calculations.minMoneyRequired / calculations.totalAfterCoupon) * 100
        : 0;
    const maxSlider =
      calculations.totalAfterCoupon > 0
        ? (calculations.maxMoneyAllowed / calculations.totalAfterCoupon) * 100
        : 100;
    return { minSlider, maxSlider };
  }, [calculations]);

  const handleChange = useCallback(
    (event, newValue) => {
      if (disabled) return;
      const invertedValue = 100 - newValue;
      const clampedValue = Math.max(
        sliderLimits.minSlider,
        Math.min(sliderLimits.maxSlider, invertedValue),
      );
      setSliderValue(clampedValue);
    },
    [disabled, sliderLimits],
  );

  const handleChangeCommitted = useCallback(
    (event, newValue) => {
      if (disabled) return;
      const invertedValue = 100 - newValue;
      const clampedValue = Math.max(
        sliderLimits.minSlider,
        Math.min(sliderLimits.maxSlider, invertedValue),
      );
      setSliderValue(clampedValue);

      if (onChange) {
        // Manter valores com precisão total durante cálculos
        let moneyValue = (clampedValue / 100) * calculations.totalAfterCoupon;

        // Threshold para tratar valores residuais muito pequenos (< R$ 0,05) como zero
        const MONEY_THRESHOLD = 0.05;

        if (moneyValue < MONEY_THRESHOLD && moneyValue > 0) {
          moneyValue = 0;
        }

        // Calcula pontos corretamente: subtrai o equivalente em pontos do dinheiro que será pago
        const isFullPoints = clampedValue === 0 || moneyValue === 0;
        const pointsValue = isFullPoints
          ? calculations.totalPointsAfterCoupon
          : calculations.totalPointsAfterCoupon -
            Math.round(moneyValue * calculations.safeFactor);

        onChange({
          sliderValue: clampedValue,
          moneyValue,
          pointsValue,
          couponValue: calculations.effectiveCoupon,
          moneyToPay: moneyValue,
        });
      }
    },
    [disabled, sliderLimits, calculations, onChange],
  );

  useEffect(() => {
    if (
      onChange &&
      isCouponApplied &&
      !hasInitialized &&
      calculations.safeTotal > 0
    ) {
      const initialMoneyValue = calculations.minMoneyRequired;
      const initialSliderValue =
        calculations.totalAfterCoupon > 0
          ? (initialMoneyValue / calculations.totalAfterCoupon) * 100
          : 0;

      const clampedValue = Math.max(0, Math.min(100, initialSliderValue));
      // Manter valores com precisão total durante cálculos
      let moneyValue = (clampedValue / 100) * calculations.totalAfterCoupon;

      // Threshold para tratar valores residuais muito pequenos (< R$ 0,05) como zero
      const MONEY_THRESHOLD = 0.05;

      if (moneyValue < MONEY_THRESHOLD && moneyValue > 0) {
        moneyValue = 0;
      }

      // Calcula pontos corretamente: subtrai o equivalente em pontos do dinheiro que será pago
      const isFullPoints = clampedValue === 0 || moneyValue === 0;
      const pointsValue = isFullPoints
        ? calculations.totalPointsAfterCoupon
        : calculations.totalPointsAfterCoupon -
          Math.round(moneyValue * calculations.safeFactor);

      onChange({
        sliderValue: clampedValue,
        moneyValue,
        pointsValue,
        couponValue: calculations.effectiveCoupon,
        moneyToPay: moneyValue,
      });

      setHasInitialized(true);
    }
  }, [onChange, isCouponApplied, calculations, hasInitialized]);

  useEffect(() => {
    if (!isCouponApplied) {
      setHasInitialized(false);
    }
  }, [isCouponApplied]);

  const formatPoints = (pts) => Math.round(pts).toLocaleString("pt-BR");

  const showMoneySegment = currentValues.moneyToPay > 0.01;
  const showCouponSegment = isCouponApplied && calculations.effectiveCoupon > 0;
  const hasPoints = userBalance > 0;
  const thumbPosition = currentValues.pointsPercentage;
  const rightLabel = showMoneySegment
    ? "Valor"
    : showCouponSegment
      ? "Cupom"
      : "Valor";
  const rightLabelClass = showMoneySegment
    ? "label-money"
    : showCouponSegment
      ? "label-coupon"
      : "label-money";

  // Se usuário não tem pontos, mostrar valor + cupom (se aplicado)
  if (!hasPoints) {
    const hasCouponDiscount =
      isCouponApplied && calculations.effectiveCoupon > 0;
    const moneyPercentageNoPoints =
      calculations.safeTotal > 0
        ? (calculations.totalAfterCoupon / calculations.safeTotal) * 100
        : 100;
    const couponPercentageNoPoints =
      calculations.safeTotal > 0
        ? (calculations.effectiveCoupon / calculations.safeTotal) * 100
        : 0;

    return (
      <Container>
        <SliderLabels>
          <span className="label-money">Valor</span>
          {hasCouponDiscount && <span className="label-coupon">Cupom</span>}
        </SliderLabels>

        <SliderTrack>
          <SliderSegment
            className="segment-money"
            style={{
              width: `${hasCouponDiscount ? moneyPercentageNoPoints : 100}%`,
            }}
          />
          {hasCouponDiscount && (
            <SliderSegment
              className="segment-coupon"
              style={{ width: `${couponPercentageNoPoints}%` }}
            />
          )}
        </SliderTrack>

        <SliderValues>
          <ValueItem className="value-money">
            {FormatCurrency(roundCurrency(calculations.totalAfterCoupon))}
          </ValueItem>
          {hasCouponDiscount && (
            <>
              <span className="separator">+</span>
              <ValueItem className="value-coupon">
                {FormatCurrency(roundCurrency(calculations.effectiveCoupon))}
              </ValueItem>
            </>
          )}
        </SliderValues>
      </Container>
    );
  }

  return (
    <Container>
      <SliderLabels>
        <span className="label-points">Pontos</span>
        <span className={rightLabelClass}>{rightLabel}</span>
      </SliderLabels>

      <SliderTrack>
        <SliderSegment
          className="segment-points"
          style={{ width: `${currentValues.pointsPercentage}%` }}
        />

        {showMoneySegment && (
          <SliderSegment
            className="segment-money"
            style={{ width: `${currentValues.moneyPercentage}%` }}
          />
        )}

        {showCouponSegment && (
          <SliderSegment
            className="segment-coupon"
            style={{ width: `${calculations.couponPercentage}%` }}
          />
        )}

        <SliderThumb
          className={showMoneySegment ? "thumb-money" : "thumb-coupon"}
          style={{ left: `${thumbPosition}%` }}
        />

        <Slider
          value={sliderValue}
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
          disabled={disabled}
          min={0}
          max={100}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0,
            cursor: disabled ? "not-allowed" : "pointer",
            "& .MuiSlider-thumb": { display: "none" },
            "& .MuiSlider-track": { display: "none" },
            "& .MuiSlider-rail": { display: "none" },
          }}
        />
      </SliderTrack>

      <SliderValues>
        <ValueItem className="value-points">
          {formatPoints(currentValues.pointsToUse)} pts
        </ValueItem>

        {showMoneySegment && (
          <>
            <span className="separator">+</span>
            <ValueItem className="value-money">
              {FormatCurrency(roundCurrency(currentValues.moneyToPay))}
            </ValueItem>
          </>
        )}

        {showCouponSegment && (
          <>
            <span className="separator">+</span>
            <ValueItem className="value-coupon">
              {FormatCurrency(calculations.effectiveCoupon)}
            </ValueItem>
          </>
        )}
      </SliderValues>
    </Container>
  );
}

SliderCheckoutCoupon.propTypes = {
  totalPoints: PropTypes.number,
  totalMoney: PropTypes.number,
  couponDiscount: PropTypes.number,
  userBalance: PropTypes.number,
  minPoints: PropTypes.number,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  isCouponApplied: PropTypes.bool,
  factor: PropTypes.number,
};
